import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {
  HardwareManagementGarantietypStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_GARANTIETYP
} from '../../../hardware-management.shared';
import {map} from 'rxjs/operators';
import {FormGroupDirective, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  Validators,
} from '@softline/ui-core';
import { EntityInputComponent } from '@softline/ui-core/entity-input';
import {FocusMonitor} from '@angular/cdk/a11y';
import {Store} from '@softline/core';
import {showRequestErrors} from '@softline/application';
import {GarantieTyp} from '../../../types/garantie-typ';
import {CommonModule} from '@angular/common';
import {InputBaseComponent} from '../../input-base/input-base.component';

import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-garantietyp-input',
    imports: [CommonModule, InputBaseComponent, ReactiveFormsModule,
    InputComponent,
    TranslatePipe],
    templateUrl: './garantietyp-input.component.html',
    styleUrls: ['./garantietyp-input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GarantietypInputComponent {

  @Input() readonly = false;

  readonly typInEditing$ = new BehaviorSubject<GarantieTyp | null>(null);

  readonly entities$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_GARANTIETYP,
    HardwareManagementGarantietypStore.getters.all
  ).pipe(map(o => o as GarantieTyp[]));

  get formGroup(): UntypedFormGroup {
    return this.parentFormGroup.form;
  }

  readonly typEditingForm = new UntypedFormGroup({
    typ: new UntypedFormControl(null, Validators.required()),
    bezeichnung: new UntypedFormControl(null),
    beschreibung: new UntypedFormControl(null)
  });

  constructor(
    private readonly parentFormGroup: FormGroupDirective,
    private readonly focusMonitor: FocusMonitor,
    private store: Store
  ) { }

  select(typ: GarantieTyp | null | undefined): void {
    if(!typ)
      return;
    this.formGroup.patchValue({ garantietyp: typ });
  }

  edit(entity: GarantieTyp | null | undefined): void {
    if(!entity)
      return;
    this.typInEditing$.next(entity);
    this.typEditingForm.patchValue(entity);
  }

  resetForm(typ?: GarantieTyp | null): void {
    if (!typ) {
      this.typEditingForm.reset();
      return;
    }

    this.typEditingForm.reset(typ);
  }

  async saveEdit(entity: GarantieTyp): Promise<void> {
    const updatedEntity = {
      ...entity,
      ...this.typEditingForm.value
    };

    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_GARANTIETYP,
        HardwareManagementGarantietypStore.actions.update,
        { entity: updatedEntity }
      );

      this.select(result);
      this.typEditingForm.reset();
      this.typInEditing$.next(null);

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Garantie-Typ wurde erfolgreich aktualisiert'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async create(entity: GarantieTyp): Promise<void> {
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_GARANTIETYP,
        HardwareManagementGarantietypStore.actions.create,
        { entity }
      );

      this.select(result);
      this.typEditingForm.reset();

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Garantietyp wurde erfolgreich aktualisiert'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  valueChanged(newValue: GarantieTyp | null | undefined): void {
    if (!newValue) {
      this.typEditingForm.reset();
      this.typInEditing$.next(null);
    }

    if (newValue && this.formGroup.value?.hardwaretyp?.id !== newValue?.id) {
      this.formGroup.patchValue({ garantietyp: newValue }, { onlySelf: true });
    }
  }

  canceled(entityInput?: EntityInputComponent<any>): void {
    this.formGroup.patchValue({ garantietyp: null });
    this.typEditingForm.reset();
    this.typInEditing$.next(null);

    if (entityInput) {
      // remove any existing text of the input (reset)
      entityInput.onInput('');
      entityInput.input = '';
      entityInput.inputComponent.nativeElement.value = '';
    }
  }

  closed(): void {
    this.typEditingForm.reset();
  }

  // We have to use an arrow-function in order to preserve `this` context
  readonly filterTypes = (types: GarantieTyp[], filter: string): GarantieTyp[] => {
    if (!filter || filter?.length < 1) {
      return types;
    }

    return types.filter(typ => {
      return typ.typ.toLowerCase().includes(filter)
        || typ.bezeichnung.toLowerCase().includes(filter)
        || typ.beschreibung.toLowerCase().includes(filter);
    });
  }
}
